import { Affix, Paper, SelectProps, Text } from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useSelectWorkspace } from '@/queries/workspaceQueries'
import { RouterPath } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { showToast } from '@/theme/notifications'
import { WorkspaceMemberStatus } from '@/types/workspace'
import { WorkspaceSelect } from './WorkspaceSelect'

type WorkspaceSelectHandlerProps = {
  selectProps?: Omit<SelectProps, 'data' | 'value' | 'onChange'>
}

export const WorkspaceSelectHandler = ({
  selectProps
}: WorkspaceSelectHandlerProps) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { refreshSession, isRefreshing } = useSession()
  const { mutateAsync: selectWorkspace, isPending } = useSelectWorkspace()

  const { workspaceMemberships, activeMembership } = useSession()

  const activeWorkspacesSorted = useMemo(
    () =>
      workspaceMemberships
        .filter(
          (membership) => membership.status === WorkspaceMemberStatus.Active
        )
        .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name)),
    [workspaceMemberships]
  )

  const handleWorkspaceChange = async (
    workspaceId: string | null,
    workspaceName?: string
  ) => {
    if (!workspaceId || isPending) {
      return
    }

    try {
      await selectWorkspace(workspaceId)
      await navigate(RouterPath.root)
      await refreshSession()
      // Clear the query client to ensure no cached data from the previous workspace remains
      queryClient.clear()

      if (workspaceName) {
        showToast(
          intl.formatMessage(
            { id: 'workspace.switch.success' },
            {
              workspaceName
            }
          ),
          'success'
        )
      }
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.switch.error' })
        ),
        'error'
      )
    }
  }

  return (
    <>
      <WorkspaceSelect
        value={activeMembership?.workspace.id || null}
        workspaceMemberships={activeWorkspacesSorted}
        {...selectProps}
        onChange={(id, { label }) => void handleWorkspaceChange(id, label)}
      />

      {/* Centers the Dialog horizontally. See: https://github.com/orgs/mantinedev/discussions/8103 */}
      {(isPending || isRefreshing) && (
        <Affix
          style={{
            top: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '240px'
          }}
        >
          <Paper
            bg="var(--mantine-color-orange-0)"
            p="xs"
            style={(theme) => ({
              borderColor: theme.colors.dark[5]
            })}
            shadow="xs"
            withBorder
          >
            <Text size="xs" fw={600}>
              <FormattedMessage id="general.loading" />
            </Text>
          </Paper>
        </Affix>
      )}
    </>
  )
}
